import { dehydrate, hydrate, type Query, type QueryClient } from "@tanstack/react-query"

/**
 * HQ KEEPS THE LAST LISTS AND REPORTS ON THE PHONE (owners, 21 Oct 2026: "many pages are slow to load").
 *
 * The backend answers each report in 0.1–0.7 s, but every page opened from scratch waited for all of
 * its reports before it showed anything, and an iPhone's Home Screen app starts from scratch often.
 * The lists and reports read last are therefore saved here and shown at once when HQ starts. React
 * Query then treats them as stale and refreshes them in the background, so the screen catches up a
 * moment later.
 *
 * Only LISTS and REPORTS are kept (`KEEP`), never an order, product or custom order on its own: those
 * pages fill editors from their data, and an editor started from an old copy could save old values.
 * The sign-in itself ("me") is kept too, so the app shell paints without waiting. A copy is dropped when
 * it is older than a day, when HQ is deployed again (the data's shape may have changed), on sign-out,
 * and as soon as the backend says nobody is signed in.
 */

const STORE = "hq-query-cache-v1"
const MAX_AGE_MS = 24 * 60 * 60 * 1000
/** One query bigger than this is not kept; the whole copy stays under ~3.5 MB (localStorage holds ~5). */
const MAX_QUERY_CHARS = 700_000
const MAX_TOTAL_CHARS = 3_500_000

/** The second part of a query key ("hq", <this>, …) for lists and reports that are safe to show early. */
const KEEP = new Set([
  "orders", "custom", "alerts", "pulse", "sales", "visitors", "product-profit", "product-items", "stock-outlook",
  "stock-lots", "stock-changes", "parcels", "purchase-orders", "payment-links", "money", "forecast", "insights",
  "planner", "checks", "costs", "delivery-costs", "courier", "catalog-health", "health", "cost-settings",
  "messages", "extras", "discounts", "replies", "suppliers", "corrections", "shop-order", "team",
])

/** Lists whose key is followed by an id or a form's context, which must not be kept. */
const LIST_ONLY = new Set(["orders", "custom"])

/** Catalogue site: its overview and its badges page are reports; its review lists carry photos and stay out. */
const CATALOGUE_KEEP = new Set(["overview", "coverage"])

/** A new build discards the copy: it names its own main script (`/assets/index-<hash>.js`). */
function buildId() {
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')
  return script?.getAttribute("src") ?? "dev"
}

export function keepQuery(query: Pick<Query, "queryKey" | "state">) {
  if (query.state.status !== "success" || query.state.data === undefined) return false
  const key = query.queryKey
  if (key[0] === "me") return query.state.data !== null
  if (key[0] === "hq" && key[1] === "catalogue") return typeof key[2] === "string" && CATALOGUE_KEEP.has(key[2])
  if (key[0] !== "hq" || typeof key[1] !== "string" || !KEEP.has(key[1])) return false
  return !(LIST_ONLY.has(key[1]) && key.length > 2)
}

type Saved = { build: string; at: number; state: ReturnType<typeof dehydrate> }

/** Puts the last saved lists and reports into the cache before the app first renders. */
export function restoreQueries(client: QueryClient) {
  try {
    const raw = window.localStorage.getItem(STORE)
    if (!raw) return
    const saved = JSON.parse(raw) as Saved
    if (saved.build !== buildId() || !(Date.now() - saved.at < MAX_AGE_MS)) {
      window.localStorage.removeItem(STORE)
      return
    }
    hydrate(client, saved.state)
  } catch {
    forgetQueries()
  }
}

/** Saves the lists and reports a moment after any of them changes, and when HQ is put away. */
export function persistQueries(client: QueryClient) {
  let timer: number | undefined
  const save = () => {
    if (timer) window.clearTimeout(timer)
    timer = undefined
    try {
      const state = dehydrate(client, { shouldDehydrateQuery: keepQuery })
      const sized = state.queries
        .map((query) => ({ query, chars: JSON.stringify(query.state.data).length }))
        .filter((row) => row.chars <= MAX_QUERY_CHARS)
        // The most recently read first, so a full copy keeps what was looked at last.
        .sort((a, b) => b.query.state.dataUpdatedAt - a.query.state.dataUpdatedAt)
      let total = 0
      const kept = sized.filter((row) => (total += row.chars) <= MAX_TOTAL_CHARS).map((row) => row.query)
      if (!kept.some((query) => query.queryKey[0] === "me")) return forgetQueries()
      window.localStorage.setItem(STORE, JSON.stringify({ build: buildId(), at: Date.now(), state: { ...state, queries: kept, mutations: [] } } satisfies Saved))
    } catch {
      // Storage full or blocked (private mode): HQ simply loads as it did before.
      forgetQueries()
    }
  }
  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "success" || !keepQuery(event.query)) return
    if (!timer) timer = window.setTimeout(save, 2000)
  })
  const onHide = () => {
    if (document.visibilityState === "hidden" && timer) save()
  }
  document.addEventListener("visibilitychange", onHide)
  return () => {
    unsubscribe()
    document.removeEventListener("visibilitychange", onHide)
  }
}

export function forgetQueries() {
  try {
    window.localStorage.removeItem(STORE)
  } catch {
    /* blocked storage: nothing was kept */
  }
}
